import type {GetAccount, GetFeatures} from './useServiceAccount.types';
import type {GetWhoamiResponse} from '@myparcel-frontend/iam-service';
import {IamFeatureProvider} from '@mypadev/openfeature-providers-library';
import {Feature, useIamService} from '@myparcel-frontend/iam-service';
import {MultiProvider, OpenFeature} from '@openfeature/web-sdk';
import {attemptAsync} from 'es-toolkit';
import {storeToRefs} from 'pinia';
import {computed} from 'vue';
import useAcl from '@/composables/useAcl';
import {useEnvironmentStore} from '@/env/useEnvironmentStore';
import {StaticFeatureProvider} from '@/providers/StaticFeatureProvider';
import {useServiceAccountStore} from '@/store/serviceAccountStore/serviceAccountStore';
import logToMonitor from '@/utils/logging';

function createAccountFetcher(fetchWhoami: (options?: {force?: boolean}) => Promise<GetWhoamiResponse>): {
  getAccount: GetAccount;
} {
  let pending: Promise<GetWhoamiResponse & {features: string[]}> | undefined;

  const getAccount: GetAccount = (options) => {
    if (!pending || options?.force) {
      const request = (async () => {
        const response = await fetchWhoami(options);

        return {
          ...response,
          features: response.features ?? [],
        };
      })();

      pending = request;

      request.catch((error) => {
        if (pending === request) {
          pending = undefined;
        }
        logToMonitor(error as Error, {
          _myparcel_extra_info: 'Fetching account data failed in createAccountFetcher',
        });
      });
    }

    return pending;
  };

  return {getAccount};
}

export const useServiceAccount = () => {
  const serviceAccountStore = useServiceAccountStore();
  const {account, isImpersonated} = storeToRefs(serviceAccountStore);
  const openFeatureClient = OpenFeature.getClient();
  const client = useIamService();
  const {getAccount: fetchAccount} = createAccountFetcher(client.getAccount);

  const isIamFeatureActive = (feature: Feature): boolean => {
    // NOTE: account.value?.features is not used here, because the openFeatureClient will handle the caching of the features
    // and will call getAccountFeatures when needed. This allows us to have a single source of truth for the features and to avoid potential issues with stale data in the account.value?.features.
    return openFeatureClient?.getBooleanValue(feature, false);
  };

  const isFulfilmentActive = computed(() => {
    return (account.value?.fulfilmentPlatforms?.length ?? 0) > 0;
  });

  const isSalesChannelsAccessible = computed(() => {
    return isIamFeatureActive(Feature.ORDER_MANAGEMENT) || isFulfilmentActive.value;
  });

  const isOrderRulesAccessible = computed(() => {
    return isIamFeatureActive(Feature.ORDER_MANAGEMENT);
  });

  const getAccount = async () => {
    if (account.value) {
      return account.value;
    }

    const response = await fetchAccount();
    account.value = response;

    return account.value;
  };

  const initOpenFeature = async (): Promise<void> => {
    const {environment} = useEnvironmentStore();
    const {acl} = useAcl();

    const [error] = await attemptAsync(async () => {
      const accountData = await getAccount();

      const multiProvider = new MultiProvider([
        {provider: new IamFeatureProvider({getFeatures: fetchAccount satisfies GetFeatures})},
        {
          provider: new StaticFeatureProvider({
            platform: accountData?.platform,
            accountId: acl.value.account_id,
            devMode: environment.devMode,
          }),
        },
      ]);

      await OpenFeature.setProviderAndWait(multiProvider);
    });

    if (error) {
      // We don't want the app to break if the feature provider fails, so we catch the error and log it, but we don't rethrow it.
      // This means that if the feature provider fails, all features will be treated as disabled, which is a safe fallback.
      await OpenFeature.setProviderAndWait(
        new IamFeatureProvider({getFeatures: () => Promise.resolve({features: []})}),
      );

      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Initializing multi-provider for OpenFeature failed',
      });
    }
  };

  return {
    account,
    isImpersonated,
    isFulfilmentActive,
    isSalesChannelsAccessible,
    isOrderRulesAccessible,
    getAccount,
    initOpenFeature,
    isIamFeatureActive,
  };
};
